import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, ScanLine, ArrowRight, MapPin, AlertTriangle } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { UsageGauge } from "@/components/UsageGauge";
import { ItemCard } from "@/components/ItemCard";
import { api } from "@/lib/api";
import { NONE, useCategories, useCatLabel } from "@/lib/hooks";
import { useAuth, useWs } from "@/context/Auth";
import { useI18n } from "@/i18n/I18n";

const EMPTY_IMG = "https://images.unsplash.com/photo-1782097551717-04a61d9973dd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const BigAction = ({ to, icon: Icon, label, primary, testId }) => (
  <Link to={to} data-testid={testId}
    className={`group flex items-center justify-between gap-4 rounded-2xl p-5 sm:p-6 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-lg active:scale-[0.98] ${primary ? "bg-blue-600 text-white shadow-blue-600/20" : "bg-slate-900 text-white"}`}>
    <span className="flex items-center gap-4"><span className="rounded-xl bg-white/15 p-3"><Icon className="w-7 h-7" /></span><span className="text-lg font-bold font-head">{label}</span></span>
    <ArrowRight className="w-5 h-5 opacity-60 group-hover:opacity-100 rtl:rotate-180" />
  </Link>
);

const Moves = () => {
  const { t, fmtDateTime } = useI18n();
  const { data = [] } = useQuery({ queryKey: ["movements"], queryFn: () => api.get("/movements").then((r) => r.data) });
  if (!data.length) return null;
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-bold">{t("dash.moves")}</h2>
      <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
        {data.slice(0, 6).map((m) => (
          <li key={m.id} className="p-3 text-sm" data-testid={`dash-move-${m.id}`}>
            <Link to={`/items/${m.item_id}`} className="font-semibold hover:underline">{m.item_name}</Link>
            <p className="font-mono text-xs text-slate-500 mt-1">{m.from_path.join(" → ") || "—"} <span className="text-blue-600">⇒</span> {m.to_path.join(" → ") || "—"}</p>
            <p className="text-xs text-slate-400 mt-0.5">{fmtDateTime(m.created_at)} · {t("item.movedBy", { user: m.user_name })}</p>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default function Dashboard() {
  const { t } = useI18n();
  const { me } = useAuth();
  const { plan, usage, canWrite, features } = useWs();
  const { data: items = [] } = useQuery({ queryKey: ["items", "", ""], queryFn: () => api.get("/items").then((r) => r.data) });
  const { data: cats = NONE } = useCategories();
  const catLabel = useCatLabel();
  const catById = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c])), [cats]);
  const L = plan?.limits || {};

  return (
    <div className="space-y-10">
      <PageHeader title={t("dash.hello", { name: me.user.name.split(" ")[0] })} subtitle={me.workspace.name} testId="dashboard-title" />
      {canWrite && (
        <div className="grid sm:grid-cols-2 gap-4 fade-up">
          <BigAction to="/items/new" icon={Plus} label={t("dash.quickAdd")} primary testId="dash-add-item" />
          <BigAction to="/scan" icon={ScanLine} label={t("dash.scan")} testId="dash-scan" />
        </div>
      )}
      {usage.low_stock > 0 && (
        <Link to="/items?low=1" data-testid="low-stock-alert" className="flex items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 p-5 text-red-800 transition-colors hover:bg-red-100">
          <span className="flex items-center gap-3 font-semibold"><AlertTriangle className="w-5 h-5" />{t("stock.alertTitle", { count: usage.low_stock })}</span>
          <ArrowRight className="w-4 h-4 rtl:rotate-180" />
        </Link>
      )}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-5" data-testid="dash-usage">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">{t("dash.usage")}</h2>
          <Link to="/plans" className="text-sm font-semibold text-blue-700 hover:underline" data-testid="dash-plans-link">{t(`planName.${plan.id}`)}</Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <UsageGauge label={t("dash.items")} used={usage.items} limit={L.items} testId="gauge-items" />
          <UsageGauge label={t("dash.places")} used={usage.places} limit={L.places} testId="gauge-places" />
          <UsageGauge label={t("dash.members")} used={usage.members} limit={L.members} testId="gauge-members" />
          <UsageGauge label={t("dash.ai")} used={usage.ai} limit={L.ai} testId="gauge-ai" />
        </div>
      </section>
      {usage.places === 0 ? (
        <section className="relative overflow-hidden rounded-2xl bg-slate-900 text-white" data-testid="dash-empty">
          <img src={EMPTY_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
          <div className="relative p-8 sm:p-12 space-y-5 max-w-lg">
            <MapPin className="w-8 h-8 text-amber-400" />
            <p className="text-xl font-bold font-head">{t("dash.empty")}</p>
            {canWrite && <Link to="/locations?new=1" data-testid="dash-first-place" className="inline-flex rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 hover:bg-amber-300 transition-colors">{t("dash.startPlace")}</Link>}
          </div>
        </section>
      ) : (
        <div className="grid lg:grid-cols-3 gap-8">
          <section className="lg:col-span-2 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">{t("dash.recent")}</h2>
              <Link to="/items" className="text-sm text-blue-700 hover:underline">{t("nav.items")} →</Link>
            </div>
            {items.length ? (
              <div className="grid sm:grid-cols-2 gap-3">{items.slice(0, 6).map((it) => <ItemCard key={it.id} item={it} catLabel={catLabel(catById[it.category_id])} />)}</div>
            ) : <p className="text-sm text-slate-500">{t("item.empty")}</p>}
          </section>
          {features.history && <Moves />}
        </div>
      )}
    </div>
  );
}
